import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { SectionCard, EmptyState } from "@/components/common/Primitives";
import { formatApiError } from "@/lib/api";

/**
 * fields: [{key, label, type='text'|'number'|'email'|'select'|'textarea'|'checkbox', options?, required?}]
 */
export default function CrudTable({
  title, subtitle, endpoint, fields, columns, testIdPrefix, canWrite = true, canDelete = true, searchable = true,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => {
    setEditing(null);
    const init = {};
    fields.forEach((f) => { init[f.key] = f.default ?? (f.type === "checkbox" ? true : ""); });
    setForm(init);
    setDlg(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    const init = {};
    fields.forEach((f) => { init[f.key] = row[f.key] ?? (f.type === "checkbox" ? false : ""); });
    setForm(init);
    setDlg(true);
  };
  const save = async () => {
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key]); });
      if (editing) { await api.put(`${endpoint}/${editing.id}`, payload); toast.success("Saved"); }
      else {
        const { data } = await api.post(endpoint, payload);
        toast.success("Created");
        if (data && data.formula_sync) {
          const s = data.formula_sync;
          toast.info(`Formula Engine synced: ${s.categories} categories, ${s.per_weight}% each. Review weights.`, { duration: 6000 });
        }
      }
      setDlg(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (row) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      const { data } = await api.delete(`${endpoint}/${row.id}`);
      toast.success("Deleted");
      if (data && data.formula_sync) {
        const s = data.formula_sync;
        toast.info(`Formula Engine synced: ${s.remaining} categories remaining, ${s.per_weight}% each. Review weights.`, { duration: 6000 });
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = query
    ? items.filter((it) => Object.values(it).some((v) => String(v || "").toLowerCase().includes(query.toLowerCase())))
    : items;

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      actions={
        <div className="flex items-center gap-2">
          {searchable && (
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" className="h-9 pl-8 w-56" />
            </div>
          )}
          {canWrite && (
            <Button data-testid={`${testIdPrefix}-create-button`} onClick={openNew} className="bg-sams-primary hover:bg-sams-primaryHover text-white h-9">
              <Plus size={14} className="mr-1" /> New
            </Button>
          )}
        </div>
      }
    >
      {loading ? <div className="text-sm text-slate-500">Loading...</div> :
        filtered.length === 0 ? <EmptyState /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                  {columns.map((c) => (
                    <th key={c.key} className={`text-left px-3 py-2 ${c.className || ""}`}>{c.label}</th>
                  ))}
                  {(canWrite || canDelete) && <th className="text-right px-3 py-2 w-32">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} data-testid={`${testIdPrefix}-row-${row.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    {columns.map((c) => (
                      <td key={c.key} className={`px-3 py-2 ${c.className || ""}`}>
                        {c.render ? c.render(row) : (row[c.key] ?? "—")}
                      </td>
                    ))}
                    {(canWrite || canDelete) && (
                      <td className="px-3 py-2 text-right">
                        {canWrite && (
                          <Button size="sm" variant="ghost" data-testid={`${testIdPrefix}-edit-${row.id}`} onClick={() => openEdit(row)} className="h-8 px-2 hover:bg-slate-200">
                            <Pencil size={14} />
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="sm" variant="ghost" data-testid={`${testIdPrefix}-delete-${row.id}`} onClick={() => remove(row)} className="h-8 px-2 text-red-600 hover:bg-red-50">
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Create"} {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{f.label}{f.required && " *"}</Label>
                {f.type === "select" ? (
                  <Select value={String(form[f.key] || "")} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="mt-1 h-10"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="mt-1 w-full min-h-[80px] rounded-md border border-slate-300 px-3 py-2 text-sm" />
                ) : f.type === "checkbox" ? (
                  <div className="mt-1"><input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} /></div>
                ) : (
                  <Input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1 h-10" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancel</Button>
            <Button data-testid={`${testIdPrefix}-save-button`} onClick={save} className="bg-sams-primary hover:bg-sams-primaryHover text-white">
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
